import { jsx as _jsx } from "react/jsx-runtime";
import { Filter } from "@faire/retailer-visitor-shared/components/Filters/Filter";
import { FilterName } from "@faire/retailer-visitor-shared/components/Filters/FilterName";
import { useCategoryPageFilterKeys } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
import { useCategoryPageFormattedTotalResults } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageFormattedTotalResults";
import { useIsCategoryPageFiltersLoading } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageFiltersLoading";
import { useIsCategoryPageFilterSplitEnabled } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageFilterSplitEnabled";
import { FILTERS_TIMING_FETCH_END_KEY, FILTERS_TIMING_FETCH_START_KEY, PageLoadTimingSectionName } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/consts";
import { useCurrentNavigationPath } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentNavigationPath";
import { useOneIndexedPageNumber } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useOneIndexedPageNumber";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { trackCategoriesLoadPageSectionTiming, trackCategoriesLoadPageSectionTimingTraceApiRequestEnd, trackCategoriesLoadPageSectionTimingTraceApiRequestStart, trackCategoriesLoadPageSectionTimingTraceRendered } from "@faire/web-api--source/events/categories/timing/loadPageSection";
import isEqual from "lodash/isEqual";
import React, { useLayoutEffect, useMemo, useRef } from "react";
import styled from "styled-components";
var useFiltersLoadTiming = function() {
    var isFilterSplitEnabled = useIsCategoryPageFilterSplitEnabled();
    var isFiltersLoading = useIsCategoryPageFiltersLoading();
    var filtersTimingRef = useRef(undefined);
    var navigationPath = useCurrentNavigationPath();
    var filterKeys = useCategoryPageFilterKeys();
    var pageNumber = useOneIndexedPageNumber();
    var previousFiltersRef = useRef(filterKeys);
    var pageNumberRef = useRef(pageNumber);
    var pageNumberChanged = useMemo(function() {
        if (!isEqual(previousFiltersRef.current, filterKeys)) {
            // filter keys changed -- page number is automatically reset to 1
            previousFiltersRef.current = filterKeys;
            pageNumberRef.current = 1;
            return false;
        } else if (pageNumberRef.current !== pageNumber) {
            // page number changed
            pageNumberRef.current = pageNumber;
            return true;
        }
        return false;
    }, [
        filterKeys,
        pageNumber
    ]);
    var trackMounted = useEffectEvent(function() {
        if (pageNumberChanged && isFilterSplitEnabled) {
            // filters are not re-mounted on page number change
            return;
        }
        filtersTimingRef.current = trackCategoriesLoadPageSectionTiming(navigationPath, PageLoadTimingSectionName.FILTERS);
    });
    useLayoutEffect(function() {
        trackMounted();
    }, [
        trackMounted,
        filterKeys,
        pageNumberChanged,
        isFilterSplitEnabled
    ]);
    useLayoutEffect(function() {
        if (!filtersTimingRef.current) {
            return;
        }
        if (!isFiltersLoading) {
            var _performance_getEntriesByName_slice_, _performance_getEntriesByName_slice, _performance_getEntriesByName, _performance, _performance_getEntriesByName_slice_1, _performance_getEntriesByName_slice1, _performance_getEntriesByName1, _performance1;
            var _performance_getEntriesByName_slice__startTime;
            var startFetchTime = (_performance_getEntriesByName_slice__startTime = (_performance_getEntriesByName = (_performance = performance).getEntriesByName) === null || _performance_getEntriesByName === void 0 ? void 0 : (_performance_getEntriesByName_slice = _performance_getEntriesByName.call(_performance, FILTERS_TIMING_FETCH_START_KEY).slice(-1)) === null || _performance_getEntriesByName_slice === void 0 ? void 0 : (_performance_getEntriesByName_slice_ = _performance_getEntriesByName_slice[0]) === null || _performance_getEntriesByName_slice_ === void 0 ? void 0 : _performance_getEntriesByName_slice_.startTime) !== null && _performance_getEntriesByName_slice__startTime !== void 0 ? _performance_getEntriesByName_slice__startTime : 0;
            var _performance_getEntriesByName_slice__startTime1;
            var endFetchTime = (_performance_getEntriesByName_slice__startTime1 = (_performance_getEntriesByName1 = (_performance1 = performance).getEntriesByName) === null || _performance_getEntriesByName1 === void 0 ? void 0 : (_performance_getEntriesByName_slice1 = _performance_getEntriesByName1.call(_performance1, FILTERS_TIMING_FETCH_END_KEY).slice(-1)) === null || _performance_getEntriesByName_slice1 === void 0 ? void 0 : (_performance_getEntriesByName_slice_1 = _performance_getEntriesByName_slice1[0]) === null || _performance_getEntriesByName_slice_1 === void 0 ? void 0 : _performance_getEntriesByName_slice_1.startTime) !== null && _performance_getEntriesByName_slice__startTime1 !== void 0 ? _performance_getEntriesByName_slice__startTime1 : 0;
            // don't emit API request start/end traces for cached queries
            if (isFilterSplitEnabled && startFetchTime > filtersTimingRef.current.sourceEventStart) {
                trackCategoriesLoadPageSectionTimingTraceApiRequestStart(filtersTimingRef.current, {
                    timestampOverride: startFetchTime
                });
                trackCategoriesLoadPageSectionTimingTraceApiRequestEnd(filtersTimingRef.current, {
                    timestampOverride: endFetchTime
                });
            }
            trackCategoriesLoadPageSectionTimingTraceRendered(filtersTimingRef.current);
        }
    }, [
        isFiltersLoading,
        isFilterSplitEnabled
    ]);
};
export var CategoryPageFilters = function() {
    var formattedTotalResults = useCategoryPageFormattedTotalResults();
    var isFiltersLoading = useIsCategoryPageFiltersLoading();
    useFiltersLoadTiming();
    return /*#__PURE__*/ _jsx(StyledFilter, {
        name: FilterName.LOGGED_IN_CATEGORY,
        resultsCount: formattedTotalResults !== null && formattedTotalResults !== void 0 ? formattedTotalResults : "",
        isLoading: isFiltersLoading,
        headerSeparator: true,
        isSticky: true
    });
};
var StyledFilter = styled(Filter).withConfig({
    componentId: "sc-ac744d37-0"
})([
    "margin-top:",
    ";"
], getSpacing("1x"));
